import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ServicesSection = () => {
  const services = [
    {
      id: 1,
      icon: 'FileText',
      title: 'Vertragsrecht',
      description: 'Prüfung und Beratung zu Verträgen aller Art. Schnelle Klärung rechtlicher Fragen.',
      features: ['Vertragsanalyse', 'Rechtliche Bewertung', 'Sofortige Beratung'],
      color: 'from-primary to-brand-primary',
      iconColor: 'text-primary'
    },
    {
      id: 2,
      icon: 'CreditCard',
      title: 'Schuldenberatung',
      description: 'Professionelle Hilfe bei Überschuldung und Schuldenregulierung. Wege aus der Schuldenfalle.',
      features: ['Schuldenanalyse', 'Verhandlung mit Gläubigern', 'Insolvenzberatung'],
      color: 'from-conversion-accent to-cta',
      iconColor: 'text-conversion-accent'
    },
    {
      id: 3,
      icon: 'Lightbulb',
      title: 'Urheberrecht',
      description: 'Schutz geistigen Eigentums und Beratung zu Urheberrechtsverletzungen.',
      features: ['IP-Schutz', 'Abmahnungen', 'Lizenzrecht'],
      color: 'from-trust-builder to-success',
      iconColor: 'text-trust-builder'
    },
    {
      id: 4,
      icon: 'Shield',
      title: 'Strafrecht',
      description: 'Erste Hilfe bei strafrechtlichen Problemen. Sofortige Beratung in kritischen Situationen.',
      features: ['Erstberatung', 'Verfahrenshilfe', '24/7 Notfall'],
      color: 'from-error to-destructive',
      iconColor: 'text-error'
    },
    {
      id: 5,
      icon: 'Home',
      title: 'Mietrecht',
      description: 'Unterstützung bei Mietstreitigkeiten, Kündigung und Mieterhöhung. Kompetente Beratung.',
      features: ['Mietstreitigkeiten', 'Kündigungsschutz', 'Mietminderung'],
      color: 'from-blue-500 to-blue-600',
      iconColor: 'text-blue-500'
    },
    {
      id: 6,
      icon: 'Car',
      title: 'Verkehrsrecht',
      description: 'Hilfe bei Verkehrsunfällen, Bußgeldern und Führerscheinentzug. Rechtsbeistand im Straßenverkehr.',
      features: ['Unfallabwicklung', 'Bußgeldverfahren', 'Führerscheinrecht'],
      color: 'from-orange-500 to-orange-600',
      iconColor: 'text-orange-500'
    },
    {
      id: 7,
      icon: 'Briefcase',
      title: 'Arbeitsrecht',
      description: 'Beratung zu Kündigungen, Arbeitsverträgen und Abmahnungen. Schutz Ihrer Arbeitnehmerrechte.',
      features: ['Kündigungsschutz', 'Abfindungen', 'Arbeitsverträge'],
      color: 'from-green-500 to-green-600',
      iconColor: 'text-green-500'
    },
    {
      id: 8,
      icon: 'Tag',
      title: 'Markenrecht',
      description: 'Schutz von Marken und Designs. Beratung bei Markenverletzungen und Registrierung.',
      features: ['Markenanmeldung', 'Markenschutz', 'Abmahnungen'],
      color: 'from-purple-500 to-purple-600',
      iconColor: 'text-purple-500'
    },
    {
      id: 9,
      icon: 'Heart',
      title: 'Familienrecht',
      description: 'Unterstützung bei Scheidung, Sorgerecht und Unterhaltsfragen. Einfühlsame Rechtsberatung.',
      features: ['Scheidungsrecht', 'Sorgerecht', 'Unterhaltsrecht'],
      color: 'from-pink-500 to-pink-600',
      iconColor: 'text-pink-500'
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="space-y-4"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-text-primary">
              Unsere Rechtsgebiete
            </h2>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto">
              Professionelle Beratung in allen wichtigen Rechtsbereichen. 
              Sofortige Hilfe per Telefon - transparent und verlässlich.
            </p>
          </motion.div>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {services?.map((service, index) => (
            <motion.div
              key={service?.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="group relative bg-white rounded-xl shadow-professional hover:shadow-elevated transition-all duration-300 card-hover overflow-hidden"
            >
              {/* Gradient Background */}
              <div className={`absolute inset-0 bg-gradient-to-br ${service?.color} opacity-5 group-hover:opacity-10 transition-opacity duration-300`}></div>
              
              <div className="relative p-8">
                {/* Icon */}
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br ${service?.color} mb-6`}>
                  <Icon name={service?.icon} size={32} className="text-white" />
                </div>

                {/* Content */}
                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-text-primary group-hover:text-primary transition-colors duration-300">
                    {service?.title}
                  </h3>
                  
                  <p className="text-text-secondary text-sm leading-relaxed">
                    {service?.description}
                  </p>

                  {/* Features */}
                  <ul className="space-y-2">
                    {service?.features?.map((feature, idx) => (
                      <li key={idx} className="flex items-center space-x-2 text-sm">
                        <Icon name="Check" size={16} className={service?.iconColor} />
                        <span className="text-text-secondary">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center bg-gradient-to-r from-primary to-brand-primary rounded-2xl p-12 text-white"
        >
          <div className="space-y-6">
            <h3 className="text-2xl sm:text-3xl font-bold">
              Ihr Rechtsgebiet nicht dabei?
            </h3>
            <p className="text-lg text-white/90 max-w-2xl mx-auto">
              Kein Problem! Unsere erfahrenen Anwälte beraten Sie in nahezu allen Rechtsbereichen. 
              Rufen Sie einfach an und schildern Sie Ihr Anliegen.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/legal-areas-landing">
                <Button
                  variant="outline"
                  size="lg"
                  iconName="ArrowRight"
                  iconPosition="right"
                  className="border-white text-white hover:bg-white hover:text-primary"
                >
                  Alle Rechtsgebiete ansehen
                </Button>
              </Link>
              
              <Button
                variant="default"
                size="lg"
                iconName="Phone"
                iconPosition="left"
                className="bg-conversion-accent hover:bg-cta text-white cta-button"
              >
                Jetzt anrufen - 2,99 €/Min
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;